import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

/**
 * useSubscription
 *
 * Reads the user's subscription state from `profiles` and exposes:
 *
 *   loading            - bool, true while initial fetch is in flight
 *   status             - 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | 'lifetime' | null
 *   trialEndsAt        - Date or null
 *   currentPeriodEnd   - Date or null
 *   plan               - 'annual' | 'lifetime' | null
 *   subscriptionId     - Paddle subscription_id or null
 *   paddleCustomerId   - Paddle customer_id or null
 *
 * Derived:
 *
 *   canWrite           - bool, whether user can add new receipts/trips/etc.
 *                          true if trialing & not expired, active, or lifetime
 *                          false if trial expired, canceled past period_end, etc.
 *   daysRemaining      - int, days left in trial (or null if not trialing)
 *   needsUpgrade       - bool, true if status indicates they should see an upgrade banner
 *
 *   reload()           - manually refetch subscription state
 *
 * Always assume `loading: true` until we've fetched at least once.
 */
export function useSubscription() {
  const [state, setState] = useState({
    loading: true,
    status: null,
    trialEndsAt: null,
    currentPeriodEnd: null,
    plan: null,
    subscriptionId: null,
    paddleCustomerId: null,
  })

  const reload = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }))

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setState({
        loading: false,
        status: null,
        trialEndsAt: null,
        currentPeriodEnd: null,
        plan: null,
        subscriptionId: null,
        paddleCustomerId: null,
      })
      return
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('subscription_status, subscription_id, paddle_customer_id, trial_ends_at, current_period_end, plan')
      .eq('id', user.id)
      .single()

    if (error || !data) {
      console.error('Failed to load subscription:', error)
      setState({
        loading: false,
        status: 'trialing',  // fail-open to avoid locking users out
        trialEndsAt: null,
        currentPeriodEnd: null,
        plan: null,
        subscriptionId: null,
        paddleCustomerId: null,
      })
      return
    }

    setState({
      loading: false,
      status: data.subscription_status,
      trialEndsAt: data.trial_ends_at ? new Date(data.trial_ends_at) : null,
      currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : null,
      plan: data.plan,
      subscriptionId: data.subscription_id,
      paddleCustomerId: data.paddle_customer_id,
    })
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  // Derived values
  const now = new Date()

  const trialActive =
    state.status === 'trialing' &&
    state.trialEndsAt &&
    state.trialEndsAt > now

  const paidActive =
    (state.status === 'active' || state.status === 'past_due') &&
    (!state.currentPeriodEnd || state.currentPeriodEnd > now)

  const canceledButStillActive =
    state.status === 'canceled' &&
    state.currentPeriodEnd &&
    state.currentPeriodEnd > now

  const lifetime = state.status === 'lifetime'

  const canWrite = lifetime || trialActive || paidActive || canceledButStillActive

  const daysRemaining = state.trialEndsAt
    ? Math.max(0, Math.ceil((state.trialEndsAt - now) / (1000 * 60 * 60 * 24)))
    : null

  const needsUpgrade =
    !lifetime &&
    !paidActive &&
    !canceledButStillActive &&
    (
      // expired trial / past period_end
      !canWrite ||
      // trialing but warn if <= 3 days left
      (trialActive && daysRemaining !== null && daysRemaining <= 3)
    )

  return {
    ...state,
    canWrite,
    trialActive,
    paidActive,
    lifetime,
    daysRemaining,
    needsUpgrade,
    reload,
  }
}
